/** The competitors tab's revenue estimate (internal/revest): the table cells,
 *  the line about this app, and the drawer that shows how one number came
 *  out and lets the operator pick the revenue-per-download level.
 *
 *  monthly US net ≈ new US ratings a month × downloads per rating (100, range
 *  50–200) × revenue per download × 75%. A rough figure for comparing apps:
 *  on our own apps the total came out close, app by app it missed by several
 *  times. */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type App, type RevenueEstimate, type RevenueTier } from '@/api/client'
import { qk } from '@/lib/queries'
import { Drawer, errorText, Field, KeyValue, Notice, Select, useToast, type KeyValueItem } from '@/ui'

export function usd(n: number): string {
  if (n > 0 && n < 10) return `$${n.toFixed(2)}`
  return `$${Math.round(n).toLocaleString('en-US')}`
}

/** The table's short form: $2,565 · $22.8k · $26.0M. */
export function usdShort(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `$${(n / 1_000).toFixed(1)}k`
  return usd(n)
}

const REASON: Record<Exclude<RevenueEstimate['status'], 'ok'>, string> = {
  no_history: '기록 30일 미만',
  no_growth: '평점 증가 없음',
  non_iap: '스토어 밖 결제',
}

const REASON_LONG: Record<Exclude<RevenueEstimate['status'], 'ok'>, string> = {
  no_history: '미국 평점 수 기록이 30일이 안 된다. 기록이 쌓이면 계산한다.',
  no_growth: '측정 기간 동안 미국 평점 수가 늘지 않았다. 평점이 늘어야 계산할 수 있다.',
  non_iap:
    '쇼핑·여행·SNS·금융처럼 앱 밖에서 돈을 버는 카테고리라 평점 증가로 앱스토어 매출을 짐작할 수 없다. 아래에서 등급을 고르면 계산한다.',
}

const TIER_LABEL: Record<RevenueTier, string> = {
  low: '저가',
  mid: '중간',
  high: '고가',
  hard: '하드 페이월',
  paid: '유료 앱 가격',
}

/** The table cell: the middle figure, the range under it, or why there is none. */
export function EstimateCell({ e }: { e?: RevenueEstimate }) {
  if (!e) return <span className="text-muted-foreground">—</span>
  if (e.status !== 'ok') return <span className="text-meta text-muted-foreground">{REASON[e.status]}</span>
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span className="font-medium">{usdShort(e.mid)}</span>
      <span className="text-meta text-muted-foreground">
        {usdShort(e.low)}–{usdShort(e.high)}
      </span>
    </span>
  )
}

/** The phone card's short form. */
export function estimateShort(e?: RevenueEstimate): string {
  if (!e) return ''
  return e.status === 'ok' ? `추정 ${usdShort(e.mid)}/월` : REASON[e.status]
}

export function grossingText(e?: RevenueEstimate): string {
  const g = e?.grossing
  if (!g) return '—'
  const parts: string[] = []
  if (g.genre_rank > 0) parts.push(`카테고리 #${g.genre_rank}`)
  if (g.overall_rank > 0) parts.push(`전체 #${g.overall_rank}`)
  return parts.length ? parts.join(' · ') : '100위 밖'
}

/** The table cell: the genre rank, the all-apps rank under it. */
export function GrossingCell({ e }: { e?: RevenueEstimate }) {
  const g = e?.grossing
  if (!g || (g.genre_rank === 0 && g.overall_rank === 0)) {
    return <span className="text-meta text-muted-foreground">{grossingText(e)}</span>
  }
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{g.genre_rank > 0 ? `카테고리 #${g.genre_rank}` : '카테고리 100위 밖'}</span>
      {g.overall_rank > 0 && <span className="text-meta text-muted-foreground">전체 #{g.overall_rank}</span>}
    </span>
  )
}

/** "이 앱" — ours, the estimate next to what it really received. */
export function SelfLine({ app, e, onOpen }: { app: App; e: RevenueEstimate; onOpen: () => void }) {
  return (
    <p className="text-table">
      <button type="button" className="font-medium underline-offset-2 hover:underline" onClick={onOpen}>
        {app.name}
      </button>
      <span className="text-muted-foreground">
        {' '}
        추정 {e.status === 'ok' ? `${usd(e.mid)}/월 (${usd(e.low)}–${usd(e.high)})` : REASON[e.status]}
        {e.actual && ` · 실제 ${usd(e.actual.monthly)}/월 (미국 실수령, 최근 ${e.actual.days}일)`}
      </span>
    </p>
  )
}

type TierChoice = RevenueTier | 'auto'

export function RevenueDrawer({
  open,
  onClose,
  app,
  e,
  tiers,
  ownerAppId,
}: {
  open: boolean
  onClose: () => void
  app?: App
  e?: RevenueEstimate
  tiers: Record<string, number>
  /** The app whose competitor list is cached; it is refetched after a pick. */
  ownerAppId: number
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const setTier = useMutation({
    mutationFn: (t: TierChoice) => api.setRevenueTier(app!.id, t === 'auto' ? '' : t),
    onSuccess: () => {
      toast.success('매출 등급을 바꿨다')
      qc.invalidateQueries({ queryKey: qk.competitors(ownerAppId) })
    },
    onError: (err) => toast.error(errorText(err)),
  })
  if (!app || !e) return null

  const choice: TierChoice = e.tier_auto ? 'auto' : e.tier
  const options: { value: TierChoice; label: string }[] = [
    { value: 'auto', label: app.price > 0 ? `자동 (유료 앱 가격 ${usd(app.price)})` : `자동 (중간 ${usd(tiers.mid ?? 0.38)})` },
    ...(['low', 'mid', 'high', 'hard'] as const).map((t) => ({ value: t, label: `${TIER_LABEL[t]} ${usd(tiers[t] ?? 0)}` })),
    ...(app.price > 0 ? [{ value: 'paid' as const, label: `${TIER_LABEL.paid} ${usd(app.price)}` }] : []),
  ]

  const g = e.grossing
  const items: KeyValueItem[] = [
    {
      label: '측정 기간',
      value: e.from_date && e.to_date ? `${e.from_date} → ${e.to_date} (${e.days}일)` : '기록 없음',
    },
    {
      label: '미국 평점 증가',
      value: e.status === 'ok' ? `${e.new_ratings.toLocaleString('ko-KR')}개 · 월 ${e.ratings_per_month.toFixed(1)}개` : '—',
    },
    { label: '평점 1개당 다운로드', value: '100 (범위 50–200)' },
    {
      label: '다운로드당 매출',
      value: `${usd(e.revenue_per_download)} · ${TIER_LABEL[e.tier]}${e.tier_auto ? ' (자동)' : ''}`,
    },
    { label: '실수령 비율', value: '75% (애플 수수료 15–30%)' },
    {
      label: '매출 순위 (미국)',
      value: g ? `${grossingText(e)} · ${g.genre || '카테고리 없음'} · ${g.date}` : '아직 수집 전',
    },
  ]
  if (e.actual) {
    items.push({
      label: '실제 (미국 실수령)',
      value: `${usd(e.actual.monthly)}/월 · ${e.actual.from_date} → ${e.actual.to_date} 합계 ${usd(e.actual.proceeds)}`,
    })
  }
  if (app.monthly_revenue > 0) items.push({ label: 'Sensor Tower 월 매출', value: usd(app.monthly_revenue) })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={app.name}
      meta={
        <>
          <span>{app.category || '카테고리 없음'}</span>
          <span>{app.price > 0 ? `${app.currency} ${app.price}` : '무료'}</span>
        </>
      }
    >
      <div className="space-y-5">
        {e.status === 'ok' ? (
          <div>
            <p className="text-meta text-muted-foreground">추정 월 수익 (미국, 실수령)</p>
            <p className="mt-1 text-stat font-semibold tabular-nums">{usd(e.mid)}</p>
            <p className="text-table text-muted-foreground tabular-nums">
              범위 {usd(e.low)}–{usd(e.high)}
            </p>
          </div>
        ) : (
          <Notice tone="neutral" title={REASON[e.status]}>
            {REASON_LONG[e.status]}
          </Notice>
        )}

        <KeyValue items={items} />

        <Field
          label="다운로드당 매출 등급"
          hint="자동은 유료 앱이면 가격, 무료 앱이면 중간이다. 금액은 RevenueCat 2026 보고서의 설치 60일 뒤 매출 중앙값이다."
        >
          <Select
            value={choice}
            disabled={setTier.isPending}
            onChange={(ev) => setTier.mutate(ev.target.value as TierChoice)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <p className="text-meta text-muted-foreground">
          미국 평점 증가 × 평점 1개당 다운로드 × 다운로드당 매출 × 75%. 우리 앱으로 맞춰 보면 전체 합계는 비슷했지만 앱 하나씩은
          몇 배씩 틀렸다(평점 1개당 다운로드 13–608). 순서와 규모를 보는 용도로 쓴다.
        </p>
      </div>
    </Drawer>
  )
}
